import axios from 'axios';
import { User, LoginResponse, CreateUserRequest } from '../types';
import { getApiUrl, loadAppConfig } from '../config/appConfig';
import {
  clearTenantAdminGate,
  getGateToken,
  isTenantAdminGateExpiredError,
  notifyTenantAdminGateExpired,
  TENANT_ADMIN_GATE_HEADER,
  tenantAdminUrlNeedsGateHeader,
} from '../utils/tenantAdminGate';
import { devError, devLog } from '../utils/devLog';
import { logApiErrorDetail } from '../utils/apiError';
import { clearTesoreriaFiltrosStorage } from '../utils/tesoreriaFiltrosStorage';
import { clearGrupoCajaStorage } from '../utils/grupoCajaStorage';
import {
  applyBrowserSessionHeaders,
  clearTabSession,
  clearLastUsedAuth,
  hasBrowserSession,
  markTabExplicitLogout,
  persistTabSession,
  readTabSession,
  seedSessionFromLastUsedIfNeeded,
} from '../utils/sessionAuthStore';

const api = axios.create({
  baseURL: getApiUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

loadAppConfig().then(() => {
  api.defaults.baseURL = getApiUrl();
  devLog('🔧 API Base URL configurada:', api.defaults.baseURL);
});

if (process.env.NODE_ENV === 'development') {
  process.env.NODE_TLS_REJECT_UNAUTHORIZED = '0';
}

seedSessionFromLastUsedIfNeeded();

function shouldSilenceApiConsole(): boolean {
  if (typeof window === 'undefined') return true;
  const path = window.location.pathname;
  return path === '/sistema' || path.startsWith('/sistema/');
}

function applyCsrfHeader(config: { method?: string; headers?: any }) {
  if (!config.headers) return;
  applyBrowserSessionHeaders(config.headers, config.method);
}

function isCsrfRejection(error: { response?: { status?: number; data?: unknown } }): boolean {
  if (error.response?.status !== 403) return false;
  const data = error.response.data;
  const message = typeof data === 'string' ? data : (data as { message?: string } | undefined)?.message;
  return typeof message === 'string' && message.toLowerCase().includes('csrf');
}

let refreshInFlight: Promise<void> | null = null;

const clearSessionAndRedirectToLogin = () => {
  clearTesoreriaFiltrosStorage();
  clearTabSession();
  markTabExplicitLogout();
  clearLastUsedAuth();
  clearGrupoCajaStorage();
  delete api.defaults.headers.common['Authorization'];
  sessionStorage.setItem('authRedirectReason', 'session_expired');

  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

api.interceptors.request.use(
  (config) => {
    applyCsrfHeader(config);

    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = config.headers as { delete?: (name: string) => void } & Record<string, unknown>;
      if (typeof headers.delete === 'function') {
        headers.delete('Content-Type');
      }
      delete headers['Content-Type'];
      delete headers['content-type'];
    }

    if (tenantAdminUrlNeedsGateHeader(config.url)) {
      const gateToken = getGateToken();
      if (gateToken) {
        config.headers[TENANT_ADMIN_GATE_HEADER] = gateToken;
      }
    }

    if (!shouldSilenceApiConsole()) {
      devLog('%c 🚀 Petición saliente:', 'background: #222; color: #bada55', {
        url: config.url,
        method: config.method,
        headers: config.headers,
        data: config.data,
        params: config.params,
      });
    }

    return config;
  },
  (error) => {
    if (!shouldSilenceApiConsole()) {
      devError('%c ❌ Error en la petición:', 'background: #222; color: #ff0000', error);
    }
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    if (!shouldSilenceApiConsole()) {
      devLog('%c ✅ Respuesta recibida:', 'background: #222; color: #00ff00', {
        status: response.status,
        headers: response.headers,
        data: response.data,
      });
    }
    return response;
  },
  async (error) => {
    logApiErrorDetail(error);

    if (!shouldSilenceApiConsole()) {
      if (error.response) {
        devError('%c ❌ Error de respuesta:', 'background: #222; color: #ff0000', {
          status: error.response.status,
          headers: error.response.headers,
          data: error.response.data,
        });
      } else if (error.request) {
        devError('%c ❌ Error de petición:', 'background: #222; color: #ff0000', error.request);
      } else {
        devError('%c ❌ Error:', 'background: #222; color: #ff0000', error.message);
      }
    }

    const originalRequest = error.config;
    const requestUrl = String(originalRequest?.url || '').toLowerCase();
    const isRefreshRequest = requestUrl.includes('/api/auth/refresh');
    const isLoginRequest = requestUrl.includes('/api/auth/login');
    const isResumeRequest = requestUrl.includes('/api/auth/tabs/resume');

    if (error.response?.status === 401 && isRefreshRequest) {
      clearSessionAndRedirectToLogin();
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && (isLoginRequest || isResumeRequest)) {
      return Promise.reject(error);
    }

    if (isTenantAdminGateExpiredError(error)) {
      clearTenantAdminGate();
      notifyTenantAdminGateExpired();
    }

    if (isCsrfRejection(error) && originalRequest && !originalRequest._csrfRetry && !isRefreshRequest && !isLoginRequest) {
      originalRequest._csrfRetry = true;
      if (refreshInFlight) await refreshInFlight;
      applyCsrfHeader(originalRequest);
      return api(originalRequest);
    }

    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      try {
        if (!hasBrowserSession()) {
          throw new Error('No hay sesión');
        }

        if (!refreshInFlight) {
          refreshInFlight = (async () => {
            const response = await api.post<LoginResponse>('/api/auth/refresh', {});
            const snap = readTabSession();
            persistTabSession({
              csrfToken: response.data.csrfToken,
              tabSessionId: response.data.tabSessionId,
              tenantId: response.data.tenantId ?? snap.tenantId,
              tenantName: response.data.tenantName ?? snap.tenantName,
              tenantCode: response.data.tenantCode ?? snap.tenantCode,
              roles: response.data.roles ?? snap.roles,
              username: response.data.username || snap.username,
              displayName: response.data.displayName || snap.displayName,
              updateLastUsed: true,
            });
          })().finally(() => {
            refreshInFlight = null;
          });
        }

        await refreshInFlight;
        delete originalRequest.headers['Authorization'];
        applyCsrfHeader(originalRequest);
        return api(originalRequest);
      } catch (refreshError) {
        clearSessionAndRedirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const userService = {
  login: async (username: string, password: string): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/api/auth/login', { username, password });
    return response.data;
  },

  getUsers: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/api/users');
    return response.data;
  },

  createUser: async (user: CreateUserRequest): Promise<User> => {
    const response = await api.post<User>('/api/users', {
      ...user,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    return response.data;
  },

  updateUser: async (id: number, user: Partial<User>): Promise<User> => {
    const response = await api.put<User>(`/api/users/${id}`, {
      ...user,
      updatedAt: new Date().toISOString(),
    });
    return response.data;
  },

  deleteUser: async (id: number): Promise<void> => {
    await api.delete(`/api/users/${id}`);
  },
};

export default api;
